(() => {
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveDataEnabled = navigator.connection?.saveData === true;
  const motionEnabled = 'IntersectionObserver' in window && !motionPreference.matches && !saveDataEnabled;
  const observedElements = new WeakSet();

  // Sections with anything shoppable reveal once; re-dissolving product grids
  // or social proof while visitors comparison-scroll reads as lag, not polish.
  const commerceSelector = 'product-card, .product-grid, .resource-list, .byms-product-grid, .bfc__grid, .cp';

  const revealObserver = motionEnabled
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const target = entry.target;

            if (entry.isIntersecting) {
              target.classList.add('is-revealed');
              if ('bymsRevealOnce' in target.dataset) revealObserver.unobserve(target);
              continue;
            }

            target.classList.remove('is-revealed');

            if (entry.boundingClientRect.bottom <= 0) {
              target.dataset.bymsRevealOrigin = 'up';
            } else if (entry.boundingClientRect.top >= window.innerHeight) {
              target.dataset.bymsRevealOrigin = 'down';
            }
          }
        },
        { rootMargin: '-7% 0px -10% 0px', threshold: 0.01 }
      )
    : null;

  const staggerObserver = motionEnabled
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-revealed');
            staggerObserver.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.01 }
      )
    : null;

  function registerReveal(target, variant = 'lift', once = false) {
    if (!target || observedElements.has(target)) return;

    observedElements.add(target);
    target.dataset.bymsReveal = variant;
    if (once) target.dataset.bymsRevealOnce = '';

    if (!revealObserver) {
      target.classList.add('is-revealed');
      return;
    }

    revealObserver.observe(target);
  }

  function prepareHomepageMotion() {
    const main = document.querySelector('#MainContent[data-template^="index"]');
    if (!main) return;

    const sections = Array.from(main.children).filter((element) => element.classList.contains('shopify-section'));

    for (const section of sections) {
      if (section.querySelector('.page-nav')) {
        revealObserver?.unobserve(section);
        observedElements.delete(section);
        section.classList.remove('is-revealed');
        delete section.dataset.bymsReveal;
        delete section.dataset.bymsRevealOrigin;
        delete section.dataset.bymsRevealOnce;
      }
    }

    sections
      .slice(1)
      .filter((section) => !section.querySelector('.page-nav'))
      .forEach((section) => registerReveal(section, 'lift', Boolean(section.querySelector(commerceSelector))));
  }

  // Keep product information available during fast scrolling, anchor jumps
  // and app-driven layout changes. Only individual controls animate here.
  function prepareProductMotion() {
    const main = document.querySelector('#MainContent[data-template^="product"]');
    if (!main) return;

    Array.from(main.children)
      .filter((element) => element.classList.contains('shopify-section'))
      .slice(1)
      .filter((section) => !section.querySelector('.page-nav'))
      .forEach((section) => {
        revealObserver?.unobserve(section);
        delete section.dataset.bymsReveal;
        delete section.dataset.bymsRevealOnce;
        section.classList.add('is-revealed');
      });
  }

  function prepareCategoryMotion(scope = document) {
    const entrances = [
      ['[data-byms-superhero-collection]', 'heroic'],
      ['[data-byms-carhangers-collection]', 'drop'],
      ['[data-byms-football-collection]', 'dash'],
      ['[data-byms-frames-collection]', 'focus'],
      ['[data-byms-mdf-collection]', 'lift'],
      ['[data-byms-bestsellers-collection]', 'lift'],
      ['[data-byms-newdrop-collection]', 'lift'],
    ];

    for (const [selector, variant] of entrances) {
      const root = scope.matches?.(selector) ? scope : scope.querySelector?.(selector);
      registerReveal(root?.querySelector('.bfc__header'), variant);
    }
  }

  // Product-grid cards enter with a short per-column stagger, one-shot. The
  // delay/hidden state lives on the <li> wrapper so it never fights the
  // card's own hover/press transforms. Items added later (pagination,
  // filters) that were never registered simply render visible.
  function prepareGridStagger(scope = document) {
    const grids = scope.querySelectorAll?.('.bfc__grid, .byms-product-grid') ?? [];

    for (const grid of grids) {
      if (grid.closest('[data-byms-reveal]')) continue;

      Array.from(grid.children).forEach((item, index) => {
        if (item.tagName !== 'LI' || observedElements.has(item)) return;

        observedElements.add(item);
        item.dataset.bymsStagger = '';
        item.style.setProperty('--byms-stagger-delay', `${(index % 4) * 60}ms`);

        if (!staggerObserver) {
          item.classList.add('is-revealed');
          return;
        }

        staggerObserver.observe(item);
      });
    }
  }

  function prepareMotion(scope = document) {
    prepareHomepageMotion();
    prepareProductMotion();
    prepareCategoryMotion(scope);
    prepareGridStagger(scope);
    if (motionEnabled) document.documentElement.classList.add('byms-motion-ready');
  }

  prepareMotion();
  document.addEventListener('shopify:section:load', (event) => prepareMotion(event.target));
})();
